import React from 'react';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import classNames from 'classnames';
import {
  DocumentViewerProps,
  PDFViewerActionsProps,
} from './DocumentActionsProps';
import { useFullScreenStyles } from '../useFullScreenStyles';
import ThemableIconButton from '../../../theme/components/Button/ThemableIconButton';
import useDarkTheme from '../../../theme/styles/useDarkTheme';

interface Props extends DocumentViewerProps, PDFViewerActionsProps {
  atFirstPage: boolean;
  atLastPage: boolean;
}

const PDFViewerActions: React.FC<Props> = (props: Props) => {
  const fullScreenClasses = useFullScreenStyles();
  const darkThemeClasses = useDarkTheme();

  const {
    atFirstPage,
    atLastPage,
    darkTheme = false,
    disabled = false,
    fullScreenHandle,
    maxZoom,
    minZoom,
    onFirstPageClick,
    onLastPageClick,
    onNextPageClick,
    onPrevPageClick,
    onZoomIn,
    onZoomOut,
    zoom = 1,
    zoomable = false,
  } = props;

  const isPreviousPageDisabled = disabled || atFirstPage;
  const isLastPageDisabled = disabled || atLastPage;
  const isZoomInDisabled = disabled || (!!maxZoom && zoom >= maxZoom);
  const isZoomOutDisabled = disabled || (!!minZoom && zoom <= minZoom);
  const isZoomable = zoomable && !!onZoomIn && !!onZoomOut;

  const baseClasses = classNames({
    [fullScreenClasses.fullScreenIcon]: fullScreenHandle.active,
    [darkThemeClasses.primaryTextLight]: darkTheme,
  });

  return (
    <>
      <ThemableIconButton
        aria-label="first"
        className={classNames(baseClasses, {
          [darkThemeClasses.disabledTextLight]:
            darkTheme && isPreviousPageDisabled,
        })}
        disabled={isPreviousPageDisabled}
        onClick={onFirstPageClick}
        size="small"
        title="First"
      >
        <FirstPage />
      </ThemableIconButton>
      <ThemableIconButton
        aria-label="previous"
        className={classNames(baseClasses, {
          [darkThemeClasses.disabledTextLight]:
            darkTheme && isPreviousPageDisabled,
        })}
        disabled={isPreviousPageDisabled}
        onClick={onPrevPageClick}
        size="small"
        title="Previous"
      >
        <NavigateBefore />
      </ThemableIconButton>
      <ThemableIconButton
        aria-label="next"
        className={classNames(baseClasses, {
          [darkThemeClasses.disabledTextLight]: darkTheme && isLastPageDisabled,
        })}
        disabled={isLastPageDisabled}
        onClick={onNextPageClick}
        size="small"
        title="Next"
      >
        <NavigateNext />
      </ThemableIconButton>
      <ThemableIconButton
        aria-label="last"
        className={classNames(baseClasses, {
          [darkThemeClasses.disabledTextLight]: darkTheme && isLastPageDisabled,
        })}
        disabled={isLastPageDisabled}
        onClick={onLastPageClick}
        size="small"
        title="Last"
      >
        <LastPage />
      </ThemableIconButton>
      {isZoomable && (
        <>
          <ThemableIconButton
            aria-label="zoom out"
            className={classNames(baseClasses, {
              [darkThemeClasses.disabledTextLight]:
                darkTheme && isZoomOutDisabled,
            })}
            disabled={isZoomOutDisabled}
            onClick={onZoomOut}
            size="small"
            title="Zoom Out"
          >
            <ZoomOut />
          </ThemableIconButton>
          <ThemableIconButton
            aria-label="zoom in"
            className={classNames(baseClasses, {
              [darkThemeClasses.disabledTextLight]:
                darkTheme && isZoomInDisabled,
            })}
            disabled={isZoomInDisabled}
            onClick={onZoomIn}
            size="small"
            title="Zoom In"
          >
            <ZoomIn />
          </ThemableIconButton>
        </>
      )}
    </>
  );
};

export default PDFViewerActions;
